'use client';

import { useLayoutEffect, useMemo, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import SwapTextButton from '@/components/ui/SwapTextButton/SwapTextButton';
import { getCmsButtonLinkProps } from '@/lib/cms-button-target';
import CmsImage from '@/components/ui/CmsImage';
import { ImageType, ProductInnerButton } from '@/types/product';
import { getImageUrl, getImageUrlForVariant } from '@/lib/homepage-mappers';
import { MEDIA_PLACEHOLDER } from '@/lib/media-placeholder';
import { resolveProductButtonHref } from '@/lib/product-urls';
import { resolveStrapiMediaUrl } from '@/lib/strapi-media-url';

interface ProductPpcIntroProps {
  locale: string;
  heading?: string;
  subHeading?: string;
  description?: string;
  buttons?: ProductInnerButton[];
  bgImage?: ImageType;
  productImage?: ImageType;
}

const GRAY = '#cccccc';
const BLACK = '#0a0a0a';

function wordsFromDescription(htmlOrText: string): string[] {
  const plain = htmlOrText
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (!plain) return [];
  return plain.split(/(\s+)/);
}

function resolveCmsImageSrc(image?: ImageType): string | undefined {
  if (!image) return undefined;
  return (
    getImageUrlForVariant(image as never, 'large') ||
    getImageUrl(image as never) ||
    resolveStrapiMediaUrl(image.url) ||
    undefined
  );
}

export default function ProductPpcIntro({
  locale,
  heading,
  subHeading,
  description,
  buttons = [],
  bgImage,
  productImage,
}: ProductPpcIntroProps) {
  const sectionRef = useRef<HTMLElement>(null);
  const wordsContainerRef = useRef<HTMLDivElement>(null);

  const wordParts = useMemo(() => wordsFromDescription(description ?? ''), [description]);
  const backgroundImageSrc = resolveCmsImageSrc(bgImage);
  const productImageSrc = resolveCmsImageSrc(productImage);
  const productImageAlt = productImage?.alternativeText?.trim() || '';

  useLayoutEffect(() => {
    const section = sectionRef.current;
    const container = wordsContainerRef.current;
    if (!section || !container) return;

    gsap.registerPlugin(ScrollTrigger);
    const wordEls = container.querySelectorAll<HTMLElement>('.ppc-intro-word');

    const ctx = gsap.context(() => {
      const revealEls = gsap.utils.toArray<HTMLElement>('[data-ppc-intro-reveal]');
      const visualEls = gsap.utils.toArray<HTMLElement>('[data-ppc-intro-visual]');
      const bgEls = gsap.utils.toArray<HTMLElement>('[data-ppc-intro-bg]');
      const bagEls = gsap.utils.toArray<HTMLElement>('[data-ppc-intro-bag]');
      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      if (prefersReducedMotion) {
        gsap.set([...revealEls, ...visualEls, ...bgEls, ...bagEls], { clearProps: 'all' });
        gsap.set(wordEls, { color: BLACK });
        return;
      }

      gsap.set(revealEls, { autoAlpha: 0, y: 34 });
      gsap.set(visualEls, { autoAlpha: 0, x: 90, scale: 1.04 });
      gsap.set(bgEls, { xPercent: 4, scale: 1.08, filter: 'blur(8px)' });
      gsap.set(bagEls, { autoAlpha: 0, x: 70, rotate: 2, scale: 0.96 });
      gsap.set(wordEls, { color: GRAY });

      gsap
        .timeline({
          scrollTrigger: {
            trigger: section,
            start: 'top 76%',
            once: true,
          },
        })
        .to(visualEls, {
          autoAlpha: 1,
          x: 0,
          scale: 1,
          duration: 1,
          ease: 'power3.out',
        })
        .to(
          bgEls,
          {
            xPercent: 0,
            scale: 1,
            filter: 'blur(0px)',
            duration: 1.2,
            ease: 'power3.out',
          },
          '<',
        )
        .to(
          bagEls,
          {
            autoAlpha: 1,
            x: 0,
            rotate: 0,
            scale: 1,
            duration: 1,
            ease: 'back.out(1.3)',
          },
          '<0.18',
        )
        .to(
          revealEls,
          {
            autoAlpha: 1,
            y: 0,
            duration: 0.75,
            ease: 'power3.out',
            stagger: 0.08,
          },
          '<0.08',
        );

      if (!wordEls.length) return;

      gsap
        .timeline({
          scrollTrigger: {
            trigger: section,
            start: 'top 70%',
            end: 'bottom 72%',
            scrub: 0.65,
          },
        })
        .to(wordEls, {
          color: BLACK,
          ease: 'none',
          stagger: { each: 0.025 },
        });
    }, section);

    return () => ctx.revert();
  }, [description, wordParts.length]);

  return (
    <section
      ref={sectionRef}
      data-page-reveal="false"
      className="relative isolate overflow-hidden bg-[#f4f1eb] max-sm:py-12"
    >
      {(backgroundImageSrc || productImageSrc) && (
        <div
          data-ppc-intro-visual
          className="pointer-events-none absolute inset-y-0 right-0 z-0 hidden w-[100%] overflow-hidden lg:block"
        >
          {backgroundImageSrc ? (
            <div data-ppc-intro-bg className="absolute inset-0">
              <CmsImage
                src={backgroundImageSrc}
                alt=""
                fill
                sizes="50vw"
                className="object-cover object-center"
                fallbackSrc={MEDIA_PLACEHOLDER}
              />
            </div>
          ) : null}
          {backgroundImageSrc ? (
            <div
              aria-hidden
              className="absolute inset-0 bg-[linear-gradient(90deg,rgba(244,241,235,0.98)_0%,rgba(244,241,235,0.9)_34%,rgba(244,241,235,0.44)_58%,rgba(244,241,235,0)_78%)]"
            />
          ) : null}
          {productImageSrc ? (
            <div data-ppc-intro-bag className="absolute bottom-[8%] right-[15%] h-[80%] w-[30%]">
              <CmsImage
                src={productImageSrc}
                alt={productImageAlt}
                fill
                sizes="30vw"
                className="object-contain object-bottom drop-shadow-[0_28px_42px_rgba(54,47,39,0.28)]"
                fallbackSrc={MEDIA_PLACEHOLDER}
              />
            </div>
          ) : null}
        </div>
      )}

      <div className="container relative z-10 mx-auto min-h-[520px] px-4 sm:px-6 lg:flex lg:min-h-[560px] lg:items-center lg:px-0 xl:min-h-[620px]">
        <div className="max-w-[500px]">
          <h1
            data-ppc-intro-reveal
            className="text-3xl font-semibold tracking-tight text-[#050505] sm:text-4xl lg:text-5xl lg:leading-tight"
          >
            {heading}
          </h1>
          {subHeading ? (
            <div data-ppc-intro-reveal className="mt-4">
              <h2 className="text-lg font-semibold leading-tight text-red-600 sm:text-xl">
                {subHeading}
              </h2>
              <span className="mt-3 block h-[2px] w-12 bg-red-600" aria-hidden />
            </div>
          ) : null}

          {wordParts.length > 0 ? (
            <div
              ref={wordsContainerRef}
              data-ppc-intro-reveal
              className="mt-7 max-w-[480px] text-base font-normal leading-[1.55] text-[#0a0a0a] sm:text-lg lg:text-[1.05rem]"
            >
              {wordParts.map((part, i) => {
                if (/^\s+$/.test(part)) {
                  return <span key={i}>{part}</span>;
                }
                if (!part) return null;
                return (
                  <span key={i} className="ppc-intro-word">
                    {part}
                  </span>
                );
              })}
            </div>
          ) : null}

          {buttons.length > 0 ? (
            <div
              data-ppc-intro-reveal
              className="mt-9 flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-center"
            >
              {buttons.map((button) => (
                <SwapTextButton
                  key={button.id}
                  variant={button.buttonType === 'Solid' ? 'filled' : 'outline'}
                  text={button.buttonText}
                  showArrow={button.iconAlignment === 'Right'}
                  href={resolveProductButtonHref(button.buttonUrl, locale)}
                  {...getCmsButtonLinkProps(button)}
                  aria-label={button.ariaLabel ?? button.buttonText}
                  className="w-full justify-center rounded-md px-7 sm:w-auto"
                />
              ))}
            </div>
          ) : null}

          {(backgroundImageSrc || productImageSrc) && (
            <div
              data-ppc-intro-visual
              className="relative mt-10 min-h-[260px] overflow-hidden rounded-2xl bg-[#f4f1eb] shadow-[0_24px_80px_rgba(30,26,22,0.16)] sm:min-h-[340px] lg:hidden"
              aria-hidden
            >
              {backgroundImageSrc ? (
                <div data-ppc-intro-bg className="absolute inset-0">
                  <CmsImage
                    src={backgroundImageSrc}
                    alt=""
                    fill
                    sizes="100vw"
                    className="object-cover object-center"
                    fallbackSrc={MEDIA_PLACEHOLDER}
                  />
                </div>
              ) : null}
              {productImageSrc ? (
                <div
                  data-ppc-intro-bag
                  className="absolute bottom-6 right-7 h-[70%] w-[80%] max-w-[220px]"
                >
                  <CmsImage
                    src={productImageSrc}
                    alt={productImageAlt}
                    fill
                    sizes="24vw"
                    className="object-contain object-bottom drop-shadow-[0_22px_32px_rgba(54,47,39,0.28)]"
                    fallbackSrc={MEDIA_PLACEHOLDER}
                  />
                </div>
              ) : null}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
